/*
 * Copyright 2020 The Yorkie Authors. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TimeTicket } from '@yorkie-js/sdk/src/document/time/ticket';
import { VersionVector } from '@yorkie-js/sdk/src/document/time/version_vector';
import { Indexable } from '@yorkie-js/sdk/src/document/document';
import { RHT, RHTNode, RHTWrite } from '@yorkie-js/sdk/src/document/crdt/rht';
import { CRDTElement } from '@yorkie-js/sdk/src/document/crdt/element';
import {
  RGATreeSplit,
  RGATreeSplitNode,
  RGATreeSplitNodeID,
  RGATreeSplitPos,
  RGATreeSplitPosRange,
  RestoreSpan,
  ValueChange,
} from '@yorkie-js/sdk/src/document/crdt/rga_tree_split';
import {
  ensureNoLoneSurrogate,
  ensureUTF16Boundary,
  escapeString,
} from '@yorkie-js/sdk/src/document/json/strings';
import {
  parseAttrValue,
  parseObjectValues,
} from '@yorkie-js/sdk/src/util/object';
import type * as Devtools from '@yorkie-js/sdk/src/devtools/types';
import { GCChild, GCPair } from '@yorkie-js/sdk/src/document/crdt/gc';
import {
  accAttrWrite,
  attrGCPair,
} from '@yorkie-js/sdk/src/document/crdt/tree';
import { SplayTree } from '@yorkie-js/sdk/src/util/splay_tree';
import { LLRBTree } from '@yorkie-js/sdk/src/util/llrb_tree';
import {
  DataSize,
  DocSize,
  addDataSizes,
  subDataSize,
} from '@yorkie-js/sdk/src/util/resource';

/**
 * `TextChangeType` is the type of TextChange.
 *
 */
enum TextChangeType {
  Content = 'content',
  Style = 'style',
}

/**
 * `TextValueType` is a value of Text
 * which has a attributes that expresses the text style.
 */
export interface TextValueType<A> {
  attributes?: A;
  content?: string;
}

/**
 * `TextChange` represents the changes to the text
 * when executing the edit, setstyle methods.
 */
interface TextChange<A = Indexable> extends ValueChange<TextValueType<A>> {
  type: TextChangeType;
}

/**
 * `CRDTTextValue` is a value of Text
 * which has a attributes that expresses the text style.
 * Attributes are represented by RHT.
 *
 */
export class CRDTTextValue {
  private attributes: RHT;
  private content: string;

  constructor(content: string) {
    this.attributes = RHT.create();
    this.content = content;
  }

  /**
   * `create` creates a instance of CRDTTextValue.
   */
  public static create(content: string): CRDTTextValue {
    return new CRDTTextValue(content);
  }

  /**
   * `length` returns the length of value.
   */
  public get length(): number {
    return this.content.length;
  }

  /**
   * `substring` returns a sub-string value of the given range.
   */
  public substring(indexStart: number, indexEnd: number): CRDTTextValue {
    const value = new CRDTTextValue(
      this.content.substring(indexStart, indexEnd),
    );
    value.attributes = this.attributes.deepcopy();
    return value;
  }

  /**
   * `truncate` shortens this value in place, keeping the object identity so
   * that GC pairs registered against it are not orphaned. See
   * `RGATreeSplitValue.truncate`.
   */
  public truncate(offset: number): void {
    this.content = this.content.substring(0, offset);
  }

  /**
   * `setAttr` sets attribute of the given key, updated time and value.
   */
  public setAttr(
    key: string,
    content: string,
    updatedAt: TimeTicket,
  ): RHTWrite {
    return this.attributes.set(key, content, updatedAt);
  }

  /**
   * `getAttr` returns the attributes of this value.
   */
  public getAttrs(): RHT {
    return this.attributes;
  }

  /**
   * `toString` returns the string representation of this value.
   */
  public toString(): string {
    return this.content;
  }

  /**
   * `getDataSize` returns the data usage of this value.
   */
  public getDataSize(): DataSize {
    const dataSize = { data: 0, meta: 0 };
    dataSize.data += this.content.length * 2;

    for (const node of this.attributes) {
      // A removed attribute belongs to docSize.gc, not to live.
      // `CRDTTreeNode.getDataSize` makes the same exclusion; the two halves
      // have to answer this the same way or a document's size stops being a
      // function of its content.
      if (node.isRemoved()) {
        continue;
      }

      const size = node.getDataSize();
      dataSize.meta += size.meta;
      dataSize.data += size.data;
    }

    return dataSize;
  }

  /**
   * `toJSON` returns the JSON encoding of this value.
   */
  public toJSON(): string {
    const content = escapeString(this.content);
    const attrsObj = this.attributes.toObject();
    const attrs = [];
    for (const [key, v] of Object.entries(attrsObj)) {
      // See `parseAttrValue`: a peer that stores values raw writes ones this
      // cannot parse, and rendering must not throw on them.
      const value = parseAttrValue(v);
      // A non-string is re-encoded, never interpolated: `String(value)` on an
      // object or array emits `[object Object]` and on a parsed string-shaped
      // value emits it unquoted, either of which breaks out of the JSON this
      // builds and lets a peer-chosen attribute forge structure in
      // `Document.toJSON`.
      const item =
        typeof value === 'string'
          ? `"${escapeString(key)}":"${escapeString(value)}"`
          : `"${escapeString(key)}":${JSON.stringify(value)}`;
      attrs.push(item);
    }
    attrs.sort();
    if (attrs.length === 0) {
      return `{"val":"${content}"}`;
    }
    return `{"attrs":{${attrs.join(',')}},"val":"${content}"}`;
  }

  /**
   * `getAttributes` returns the attributes of this value.
   */
  public getAttributes(): Record<string, string> {
    return this.attributes.toObject();
  }

  /**
   * `getContent` returns the internal content.
   */
  public getContent(): string {
    return this.content;
  }

  /**
   * `purge` purges the given child node.
   */
  public purge(node: GCChild): void {
    if (this.attributes && node instanceof RHTNode) {
      this.attributes.purge(node);
    }
  }

  /**
   * `getGCPairs` returns the pairs of GC.
   */
  public getGCPairs(): Array<GCPair> {
    const pairs = [];

    for (const node of this.attributes) {
      if (node.getRemovedAt()) {
        // `getDataSize` skips removed attributes, so a tombstoned attribute
        // is not part of the live size this root was built with. Registering
        // it without `gcOnlySize` would debit live for bytes it never held.
        pairs.push({
          parent: this,
          child: node,
          gcOnlySize: node.getDataSize(),
        });
      }
    }

    return pairs;
  }
}

/**
 * `indexedContent` returns the text the given node contributes to the index:
 * empty for the head node, which holds no value, and for a tombstone, whose
 * text the index no longer counts.
 */
function indexedContent(node: RGATreeSplitNode<CRDTTextValue>): string {
  const value: CRDTTextValue | undefined = node.getValue();
  return node.isRemoved() || !value ? '' : value.getContent();
}

/**
 * `neighborContent` returns the content of the nearest node on the given side
 * that still contributes text, or an empty string when there is none.
 */
function neighborContent(
  node: RGATreeSplitNode<CRDTTextValue>,
  step: (
    n: RGATreeSplitNode<CRDTTextValue>,
  ) => RGATreeSplitNode<CRDTTextValue> | undefined,
): string {
  for (let n = step(node); n; n = step(n)) {
    const content = indexedContent(n);
    if (content) {
      return content;
    }
  }

  return '';
}

/**
 *  `CRDTText` is a custom CRDT data type to represent the contents of text editors.
 *
 */
export class CRDTText<A extends Indexable = Indexable> extends CRDTElement {
  private rgaTreeSplit: RGATreeSplit<CRDTTextValue>;

  constructor(
    rgaTreeSplit: RGATreeSplit<CRDTTextValue>,
    createdAt: TimeTicket,
  ) {
    super(createdAt);
    this.rgaTreeSplit = rgaTreeSplit;
  }

  /**
   * `create` a instance of Text.
   */
  public static create<A extends Indexable>(
    rgaTreeSplit: RGATreeSplit<CRDTTextValue>,
    createdAt: TimeTicket,
  ): CRDTText<A> {
    return new CRDTText<A>(rgaTreeSplit, createdAt);
  }

  /**
   * `edit` edits the given range with the given value and attributes.
   */
  public edit(
    range: RGATreeSplitPosRange,
    content: string,
    editedAt: TimeTicket,
    attributes?: Record<string, string>,
    versionVector?: VersionVector,
  ): [
    Array<TextChange<A>>,
    Array<GCPair>,
    DataSize,
    RGATreeSplitPosRange,
    Array<CRDTTextValue>,
    Array<RestoreSpan<CRDTTextValue>>,
  ] {
    const crdtTextValue = content ? CRDTTextValue.create(content) : undefined;
    if (crdtTextValue && attributes) {
      for (const [k, v] of Object.entries(attributes)) {
        crdtTextValue.setAttr(k, v, editedAt);
      }
    }

    const [caretPos, pairs, diff, valueChanges, removedValues, removedSpans] =
      this.rgaTreeSplit.edit(range, editedAt, crdtTextValue, versionVector);

    const changes: Array<TextChange<A>> = valueChanges.map((change) => ({
      ...change,
      value: change.value
        ? {
            attributes: parseObjectValues<A>(change.value.getAttributes()),
            content: change.value.getContent(),
          }
        : {
            attributes: undefined,
            content: '',
          },
      type: TextChangeType.Content,
    }));

    return [
      changes,
      pairs,
      diff,
      [caretPos, caretPos],
      removedValues,
      removedSpans,
    ];
  }

  /**
   * `restore` re-establishes removed characters under their original
   * identities (identity-preserving undo of a deletion).
   */
  public restore(
    spans: Array<RestoreSpan<CRDTTextValue>>,
    executedAt: TimeTicket,
    fallbackAnchor?: RGATreeSplitPos,
  ): [
    Array<RGATreeSplitNode<CRDTTextValue>>,
    Array<RGATreeSplitNode<CRDTTextValue>>,
    Array<TextChange<A>>,
    DataSize,
    Array<GCPair>,
  ] {
    const [untombstoned, recreated, valueChanges, liveDiff, pendingGCPairs] =
      this.rgaTreeSplit.restore(spans, executedAt, fallbackAnchor);
    return [
      untombstoned,
      recreated,
      this.toTextChanges(valueChanges),
      liveDiff,
      pendingGCPairs,
    ];
  }

  /**
   * `retombstone` re-deletes previously restored characters (redo).
   */
  public retombstone(
    spans: Array<RestoreSpan<CRDTTextValue>>,
    executedAt: TimeTicket,
  ): [Array<GCPair>, Array<TextChange<A>>, DataSize] {
    const [pairs, valueChanges, diff] = this.rgaTreeSplit.retombstone(
      spans,
      executedAt,
    );
    return [pairs, this.toTextChanges(valueChanges), diff];
  }

  /**
   * `toTextChanges` wraps raw RGATreeSplit value changes into `TextChange`s,
   * mirroring the mapping used by `edit`.
   */
  private toTextChanges(
    valueChanges: Array<ValueChange<CRDTTextValue>>,
  ): Array<TextChange<A>> {
    return valueChanges.map((change) => ({
      ...change,
      value: change.value
        ? {
            attributes: parseObjectValues<A>(change.value.getAttributes()),
            content: change.value.getContent(),
          }
        : {
            attributes: undefined,
            content: '',
          },
      type: TextChangeType.Content,
    }));
  }

  /**
   * `setStyle` applies the style of the given range.
   * 01. split nodes with from and to
   * 02. style nodes between from and to
   *
   * @param range - range of RGATreeSplitNode
   * @param attributes - style attributes
   * @param editedAt - edited time
   */
  public setStyle(
    range: RGATreeSplitPosRange,
    attributes: Record<string, string>,
    editedAt: TimeTicket,
    versionVector?: VersionVector,
  ): [
    Array<GCPair>,
    DocSize,
    Array<TextChange<A>>,
    Map<string, string>,
    Array<string>,
  ] {
    const size = { live: { data: 0, meta: 0 }, gc: { data: 0, meta: 0 } };

    // 01. split nodes with from and to
    const [, diffTo, toRight] = this.rgaTreeSplit.findNodeWithSplit(
      range[1],
      editedAt,
    );
    const [, diffFrom, fromRight] = this.rgaTreeSplit.findNodeWithSplit(
      range[0],
      editedAt,
    );

    addDataSizes(size.live, diffTo, diffFrom);

    // 02. style nodes between from and to
    const changes: Array<TextChange<A>> = [];
    const nodes = this.rgaTreeSplit.findBetween(fromRight, toRight);
    const toBeStyleds: Array<RGATreeSplitNode<CRDTTextValue>> = [];

    for (const node of nodes) {
      if (node.canStyle(versionVector)) {
        toBeStyleds.push(node);
      }
    }

    // Capture previous attribute values from the first styled node for reverse op
    const prevAttributes = new Map<string, string>();
    const attributesToRemove: Array<string> = [];
    let capturedPrev = false;

    // The reverse operation restores what the VISIBLE text held, so the prior
    // values come from the first LIVE node in the range. `canStyle` now admits
    // tombstones, and the first node in the range can be one -- capturing from
    // it made an undo write an attribute onto text that never carried it, out
    // of a run the user had already deleted. The fallback to the first node
    // keeps an all-tombstone range undoable.
    const captureFrom =
      toBeStyleds.find((n) => !n.isRemoved()) ?? toBeStyleds[0];

    const pairs: Array<GCPair> = [];
    for (const node of toBeStyleds) {
      // `canStyle` admits a node removed CONCURRENTLY with this style, which
      // has to be styled for the replicas to agree. It is not part of the
      // rendered text, though, so it reports no change to editors and its
      // bytes move through gc rather than live.
      const nodeIsLive = !node.isRemoved();

      if (!capturedPrev && node === captureFrom) {
        for (const key of Object.keys(attributes)) {
          const attrs = node.getValue().getAttrs();
          if (attrs.has(key)) {
            prevAttributes.set(key, attrs.get(key)!);
          } else {
            attributesToRemove.push(key);
          }
        }
        capturedPrev = true;
      }

      if (nodeIsLive) {
        const [fromIdx, toIdx] = this.rgaTreeSplit.findIndexesFromRange(
          node.createPosRange(),
        );
        changes.push({
          type: TextChangeType.Style,
          actor: editedAt.getActorID(),
          from: fromIdx,
          to: toIdx,
          value: {
            attributes: parseObjectValues(attributes) as A,
          },
        });
      }

      for (const [key, value] of Object.entries(attributes)) {
        accAttrWrite(
          node.getValue().setAttr(key, value, editedAt),
          node.getValue(),
          nodeIsLive,
          pairs,
          size,
        );
      }
    }

    pairs.push(...this.rgaTreeSplit.drainPendingGCPairs());

    return [pairs, size, changes, prevAttributes, attributesToRemove];
  }

  /**
   * `removeStyle` removes the style attributes of the given range.
   * Returns previous attribute values (from first styled node) for reverse operation.
   */
  public removeStyle(
    range: RGATreeSplitPosRange,
    attributesToRemove: Array<string>,
    editedAt: TimeTicket,
    versionVector?: VersionVector,
  ): [Array<GCPair>, DocSize, Array<TextChange<A>>, Map<string, string>] {
    const size = { live: { data: 0, meta: 0 }, gc: { data: 0, meta: 0 } };

    // 01. split nodes with from and to
    const [, diffTo, toRight] = this.rgaTreeSplit.findNodeWithSplit(
      range[1],
      editedAt,
    );
    const [, diffFrom, fromRight] = this.rgaTreeSplit.findNodeWithSplit(
      range[0],
      editedAt,
    );

    addDataSizes(size.live, diffTo, diffFrom);

    // 02. find nodes to remove style from
    const changes: Array<TextChange<A>> = [];
    const nodes = this.rgaTreeSplit.findBetween(fromRight, toRight);
    const toBeStyleds: Array<RGATreeSplitNode<CRDTTextValue>> = [];

    for (const node of nodes) {
      if (node.canStyle(versionVector)) {
        toBeStyleds.push(node);
      }
    }

    // Capture previous attribute values from the first styled node for reverse op
    const prevAttributes = new Map<string, string>();
    let capturedPrev = false;

    // The reverse operation restores what the VISIBLE text held, so the prior
    // values come from the first LIVE node in the range. `canStyle` now admits
    // tombstones, and the first node in the range can be one -- capturing from
    // it made an undo restore a value the visible text never held. The fallback to the first node
    // keeps an all-tombstone range undoable.
    const captureFrom =
      toBeStyleds.find((n) => !n.isRemoved()) ?? toBeStyleds[0];

    const pairs: Array<GCPair> = [];
    for (const node of toBeStyleds) {
      // See setStyle: a node removed concurrently with this change is styled
      // but is not part of the rendered text.
      const nodeIsLive = !node.isRemoved();

      if (!capturedPrev && node === captureFrom) {
        for (const key of attributesToRemove) {
          const attrs = node.getValue().getAttrs();
          if (attrs.has(key)) {
            prevAttributes.set(key, attrs.get(key)!);
          }
        }
        capturedPrev = true;
      }

      if (nodeIsLive) {
        const [fromIdx, toIdx] = this.rgaTreeSplit.findIndexesFromRange(
          node.createPosRange(),
        );

        const removedAttributes: Record<string, any> = {};
        for (const key of attributesToRemove) {
          removedAttributes[key] = null; // null signals attribute removal to editors (e.g., Quill)
        }
        changes.push({
          type: TextChangeType.Style,
          actor: editedAt.getActorID(),
          from: fromIdx,
          to: toIdx,
          value: {
            attributes: removedAttributes as A,
          },
        });
      }

      for (const key of attributesToRemove) {
        // `canStyle` admits a node removed concurrently with this change, so
        // the NODE holding the attribute may itself be a tombstone -- the
        // third case `attrGCPair` asks about.
        let attrWasLive = node.getValue().getAttrs().has(key);
        const { gcNodes, valueDropped } = node
          .getValue()
          .getAttrs()
          .remove(key, editedAt);

        // The tombstone is not charged for its value, so those bytes leave
        // whichever side was holding them: live for a live attribute on a live
        // node, and the node's own gc charge when the node is a tombstone --
        // the container skips a removed node, so live never held it. The same
        // split `attrGCPair` makes just below.
        subDataSize(nodeIsLive ? size.live : size.gc, valueDropped);

        for (const rhtNode of gcNodes) {
          pairs.push(
            attrGCPair(node.getValue(), rhtNode, attrWasLive, nodeIsLive),
          );
          // Only the node that replaces the live value settles the live
          // value's bytes; a second one in the same call is the tombstone it
          // superseded, which was never in live.
          attrWasLive = false;
        }
      }
    }

    pairs.push(...this.rgaTreeSplit.drainPendingGCPairs());

    return [pairs, size, changes, prevAttributes];
  }

  /**
   * `indexRangeToPosRange` returns the position range of the given index range.
   */
  public indexRangeToPosRange(
    fromIdx: number,
    toIdx: number,
  ): RGATreeSplitPosRange {
    const fromPos = this.rgaTreeSplit.indexToPos(fromIdx);
    if (fromIdx === toIdx) {
      return [fromPos, fromPos];
    }

    return [fromPos, this.rgaTreeSplit.indexToPos(toIdx)];
  }

  /**
   * `createRange` returns the position range of the given index range for a
   * local edit or style. Unlike `indexRangeToPosRange`, it rejects an index
   * that splits a UTF-16 surrogate pair.
   *
   * `content` is given for an edit and omitted for a style. It is checked for
   * lone surrogates: storing one diverges across SDKs on its own, and the half
   * then pairs with whatever code unit it is stored next to, which would turn
   * the index at that seam into one `validateUTF16Boundary` refuses for the
   * lifetime of the text. A peer running an SDK without this check can still
   * send such content — the guard is a local-edit contract, not a trust
   * boundary — but nothing a client of this SDK does can create it.
   */
  public createRange(
    fromIdx: number,
    toIdx: number,
    content?: string,
  ): RGATreeSplitPosRange {
    if (content) {
      ensureNoLoneSurrogate(content);
    }

    const range = this.indexRangeToPosRange(fromIdx, toIdx);
    this.validateUTF16Boundary(range[0]);
    if (fromIdx !== toIdx) {
      this.validateUTF16Boundary(range[1]);
    }

    return range;
  }

  /**
   * `validateUTF16Boundary` throws when the given position splits a surrogate
   * pair. At either end of the node it reads the neighbouring node: an edit or
   * a style carrying a mid-pair offset splits the node there, so a pair can
   * sit in two nodes on this replica while it is one node on every other, and
   * an index at that seam is still inside it. `indexToPos` resolves a seam to
   * the node on its left, so the `offset === content.length` side is the one
   * an index normally reaches.
   */
  private validateUTF16Boundary(pos: RGATreeSplitPos): void {
    const node = this.rgaTreeSplit.findNode(pos.getID());
    const offset = pos.getRelativeOffset();
    const content = indexedContent(node);

    let before = content.charCodeAt(offset - 1);
    let after = content.charCodeAt(offset);
    if (offset === 0) {
      const prev = neighborContent(node, (n) => n.getPrev());
      before = prev.charCodeAt(prev.length - 1);
    }
    if (offset === content.length) {
      after = neighborContent(node, (n) => n.getNext()).charCodeAt(0);
    }

    ensureUTF16Boundary(before, after);
  }

  /**
   * `length` returns size of RGATreeList.
   */
  public get length(): number {
    return this.rgaTreeSplit.length;
  }

  /**
   * `getTreeByIndex` returns the tree by index for debugging.
   */
  public getTreeByIndex(): SplayTree<CRDTTextValue> {
    return this.rgaTreeSplit.getTreeByIndex();
  }

  /**
   * `getTreeByID` returns the tree by ID for debugging.
   */
  public getTreeByID(): LLRBTree<
    RGATreeSplitNodeID,
    RGATreeSplitNode<CRDTTextValue>
  > {
    return this.rgaTreeSplit.getTreeByID();
  }

  /**
   * `refinePos` refines the given RGATreeSplitPos.
   */
  public refinePos(pos: RGATreeSplitPos): RGATreeSplitPos {
    return this.rgaTreeSplit.refinePos(pos);
  }

  /**
   * `normalizePos` normalizes the given RGATreeSplitPos.
   */
  public normalizePos(pos: RGATreeSplitPos): RGATreeSplitPos {
    return this.rgaTreeSplit.normalizePos(pos);
  }

  /**
   * `getDataSize` returns the data usage of this element.
   */
  public getDataSize(): DataSize {
    const dataSize = { data: 0, meta: 0 };
    for (const node of this.rgaTreeSplit) {
      if (node.isRemoved()) {
        continue;
      }

      const size = node.getDataSize();
      dataSize.data += size.data;
      dataSize.meta += size.meta;
    }

    return {
      data: dataSize.data,
      meta: dataSize.meta + this.getMetaUsage(),
    };
  }

  /**
   * `toJSON` returns the JSON encoding of this text.
   */
  public toJSON(): string {
    const json = [];

    for (const node of this.rgaTreeSplit) {
      if (!node.isRemoved()) {
        json.push(node.getValue().toJSON());
      }
    }

    return `[${json.join(',')}]`;
  }

  /**
   * `toSortedJSON` returns the sorted JSON encoding of this text.
   */
  public toSortedJSON(): string {
    return this.toJSON();
  }

  /**
   * `toJSForTest` returns value with meta data for testing.
   */
  public toJSForTest(): Devtools.JSONElement {
    return {
      createdAt: this.getCreatedAt().toTestString(),
      value: JSON.parse(this.toJSON()),
      type: 'YORKIE_TEXT',
    };
  }

  /**
   * `toString` returns the string representation of this text.
   */
  public toString(): string {
    return this.rgaTreeSplit.toString();
  }

  /**
   * `values` returns the content-attributes pair array of this text.
   */
  public values(): Array<TextValueType<A>> {
    const values = [];

    for (const node of this.rgaTreeSplit) {
      if (!node.isRemoved()) {
        const value = node.getValue();
        values.push({
          attributes: parseObjectValues<A>(value.getAttributes()),
          content: value.getContent(),
        });
      }
    }

    return values;
  }

  /**
   * `getRGATreeSplit` returns rgaTreeSplit.
   */
  public getRGATreeSplit(): RGATreeSplit<CRDTTextValue> {
    return this.rgaTreeSplit;
  }

  /**
   * `toTestString` returns a String containing the meta data of this value
   * for debugging purpose.
   */
  public toTestString(): string {
    return this.rgaTreeSplit.toTestString();
  }

  /**
   * `deepcopy` copies itself deeply.
   */
  public deepcopy(): CRDTText<A> {
    const clone = new CRDTText<A>(
      this.rgaTreeSplit.deepcopy(),
      this.getCreatedAt(),
    );
    clone.setRemovedAt(this.getRemovedAt());
    clone.setMovedAt(this.getMovedAt());
    return clone;
  }

  /**
   * `findIndexesFromRange` returns pair of integer offsets of the given range.
   */
  public findIndexesFromRange(range: RGATreeSplitPosRange): [number, number] {
    return this.rgaTreeSplit.findIndexesFromRange(range);
  }

  /**
   * `posToIndex` converts the given position to index.
   */
  public posToIndex(
    pos: RGATreeSplitPos,
    preferToLeft: boolean = false,
  ): number {
    return this.rgaTreeSplit.posToIndex(pos, preferToLeft);
  }

  /**
   * `getGCPairs` returns the pairs of GC.
   */
  public getGCPairs(): Array<GCPair> {
    const pairs: Array<GCPair> = [];
    // NOTE: Called when the text is registered (snapshot load, a Set/Add/
    // ArraySet payload, an undo re-set), where docSize.live counted visible
    // nodes only. Tombstoned nodes (and the
    // attribute tombstones inside them) were never part of live, so their
    // pairs carry `gcOnlySize`. So do the attribute tombstones of visible
    // nodes: `CRDTTextValue.getDataSize` skips removed attributes, matching
    // the tree half, so those bytes are not in live either.
    for (const node of this.rgaTreeSplit) {
      if (node.getRemovedAt()) {
        pairs.push({
          parent: this.rgaTreeSplit,
          child: node,
          gcOnlySize: node.getDataSize(),
        });
      }

      for (const p of node.getValue().getGCPairs()) {
        pairs.push(
          node.getRemovedAt() ? { ...p, gcOnlySize: p.child.getDataSize() } : p,
        );
      }
    }

    return pairs;
  }
}
